import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Select from "react-select/async";
import { components } from "react-select";
import debounce from "debounce-promise";
import Fuse from "fuse.js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import MenuList from "./MenuList";
import { getCFOSelectProps } from "./cfoSelectClassNames.js";

// Memoize icon outside component
const checkIcon = icon({ name: "check", style: "light" });

// Custom ValueContainer - defined outside to prevent recreation
const CustomValueContainer = ({ children, ...props }) => (
	<components.ValueContainer className="whitespace-nowrap" {...props}>
		{children}
	</components.ValueContainer>
);

// Null components - defined once outside
const NullComponent = () => null;

// Layout-only styles (CFO tokens handle appearance via classNames)
const createLayoutStyles = (dropdownWidth) => ({
	menu: (baseStyles) => ({
		...baseStyles,
		width: dropdownWidth ? `${dropdownWidth}px` : undefined,
	}),
	container: (baseStyles) => ({
		...baseStyles,
		margin: 0,
		padding: 0,
		width: "max-content",
		minWidth: "100%",
	}),
});

export const Filter = ({
	options,
	fuzzyOptions,
	wait = 300,
	width,
	dropdownWidth,
	title,
	category,
	selectedValues,
	multi,
	selectAll,
	deselectAll,
	removeFilter,
	hideButtons,
	isSearchable,
	textColor,
	showCategory,
	backgroundColor,
	onLoadMore,
	hasMore,
	isLoadingMore,
	...props
}) => {
	const [singleSelect, setSingleSelect] = useState(true);
	const fuseRef = useRef(null);
	const fuzzyOptionsRef = useRef(fuzzyOptions);
	const fuzzyOptionsSerializedRef = useRef(JSON.stringify(fuzzyOptions));

	// Create Set for O(1) lookup instead of O(n) array.includes
	const selectedValuesSet = useMemo(() => {
		if (typeof selectedValues === "string") {
			return new Set([selectedValues]);
		}
		return new Set(selectedValues);
	}, [selectedValues]);

	// Memoize value description to prevent recalculation
	const valueDescription = useMemo(() => {
		const isMultiMode = multi || !singleSelect;
		if (isMultiMode) {
			if (selectedValues.length === 1) {
				return `${title} : ${selectedValues[0]}`;
			} else if (selectedValues.length === 0) {
				return `${title} : All`;
			}
			return `${title} : ${selectedValues.length} items selected`;
		}
		return `${title} : ${selectedValues}`;
	}, [multi, singleSelect, selectedValues, title]);

	// Memoize isSelected check using Set
	const isSelected = useCallback((term) => {
		return selectedValuesSet.has(term);
	}, [selectedValuesSet]);

	// Toggle single/multi select handlers
	const enableMultiSelect = useCallback(() => setSingleSelect(false), []);
	const enableSingleSelect = useCallback(() => setSingleSelect(true), []);

	// Memoize the single/multi select toggle UI
	const singleMultiSelectUI = useMemo(() => (
		<div>
			{singleSelect ? (
				<button
					className="align-right mt-1 ml-5 mr-2 cursor-pointer hover:text-text hover:font-semibold"
					onClick={enableMultiSelect}
				>
					Multi select
				</button>
			) : (
				<button
					className="align-right mt-1 ml-5 mr-2 cursor-pointer hover:text-text hover:font-semibold"
					onClick={enableSingleSelect}
				>
					Single select
				</button>
			)}
		</div>
	), [singleSelect, enableMultiSelect, enableSingleSelect]);

	// Handle remove filter click
	const handleRemoveFilter = useCallback(() => {
		removeFilter(category);
	}, [removeFilter, category]);

	// Memoize Menu component to prevent recreation
	const CustomMenu = useCallback((menuProps) => (
		<components.Menu {...menuProps}>
			<div>
				{menuProps.selectProps.fetchingData ? (
					<span className="fetching">Fetching data...</span>
				) : (
					<div>{menuProps.children}</div>
				)}

				{!hideButtons && (
					<div className="border-t border-border flex flex-row my-2 cursor-default">
						<div className="flex-1 text-xs">{singleMultiSelectUI}</div>
						<div className="flex-end text-xs">
							<button
								className="align-right mt-1 mr-4 cursor-pointer hover:text-text hover:font-semibold"
								onClick={handleRemoveFilter}
							>
								Remove filter
							</button>
						</div>
					</div>
				)}
			</div>
		</components.Menu>
	), [hideButtons, singleMultiSelectUI, handleRemoveFilter]);

	// Handle selection change
	const onHandleChange = props.handleChange;
	const handleChange = useCallback((filter) => {
		onHandleChange(filter, category, singleSelect);
	}, [onHandleChange, category, singleSelect]);

	// Memoize fuzzyOptions to prevent unnecessary Fuse recreation
	// Compare serialized versions to detect actual changes in the configuration object
	const serializedFuzzyOptions = JSON.stringify(fuzzyOptions);
	const memoizedFuzzyOptions = useMemo(() => {
		// Only update if the serialized version actually changed
		if (fuzzyOptionsSerializedRef.current !== serializedFuzzyOptions) {
			fuzzyOptionsSerializedRef.current = serializedFuzzyOptions;
			fuzzyOptionsRef.current = fuzzyOptions;
		}
		return fuzzyOptionsRef.current;
	}, [serializedFuzzyOptions, fuzzyOptions]);

	// Initialize Fuse instance only when options or memoized fuzzyOptions change
	useEffect(() => {
		fuseRef.current = new Fuse(options, memoizedFuzzyOptions);
	}, [options, memoizedFuzzyOptions]);

	// Memoize search function
	const searchOptions = useCallback((inputValue) => {
		if (!fuseRef.current) return Promise.resolve([]);
		return Promise.resolve(
			fuseRef.current.search(inputValue).map((c) => ({ ...c.item }))
		);
	}, []);

	// Memoize debounced load options - critical for preventing recreation
	const debouncedLoadOptions = useMemo(
		() => debounce(searchOptions, wait),
		[searchOptions, wait]
	);

	// Memoize layout styles
	const selectStyles = useMemo(
		() => createLayoutStyles(dropdownWidth),
		[dropdownWidth]
	);

	// Create MenuList wrapper - pagination props are passed via selectProps
	const PaginatedMenuList = useCallback((menuListProps) => (
		<MenuList {...menuListProps} />
	), []);

	// Memoize components object to prevent recreation
	const selectComponents = useMemo(() => ({
		DropdownIndicator: NullComponent,
		IndicatorSeparator: NullComponent,
		IndicatorsContainer: NullComponent,
		MenuList: PaginatedMenuList,
		ValueContainer: CustomValueContainer,
		Menu: CustomMenu,
	}), [CustomMenu, PaginatedMenuList]);

	// Memoize formatOptionLabel
	const formatOptionLabel = useCallback((e) => {
		const selected = isSelected(e.label);
		return (
			<div>
				<div
					className="px-3 py-2 h-8 align-text-bottom"
					style={{ display: "flex-row" }}
				>
					{showCategory && (
						<span className="ml-0 text-xs text-text-2">
							{e.category}
						</span>
					)}
					<span className="ml-2">{e.label}</span>
					{selected && (
						<span className="mt-0 right-4 absolute">
							<FontAwesomeIcon
								icon={checkIcon}
								className="ml-2 text-right"
							/>
						</span>
					)}
				</div>
			</div>
		);
	}, [isSelected, showCategory]);

	// Memoize default value
	const defaultValue = useMemo(
		() => ({ label: valueDescription, value: valueDescription }),
		[valueDescription]
	);

	// Memoize paginationProps to prevent unnecessary re-renders
	const paginationProps = useMemo(() => ({
		onLoadMore,
		hasMore,
		isLoadingMore,
	}), [onLoadMore, hasMore, isLoadingMore]);

	return (
		<Select
			{...getCFOSelectProps()}
			isMulti={multi || !singleSelect}
			controlShouldRenderValue={false}
			hideSelectedOptions={false}
			placeholder={valueDescription}
			defaultOptions={options}
			loadOptions={debouncedLoadOptions}
			isSearchable={isSearchable}
			components={selectComponents}
			defaultValue={defaultValue}
			onChange={handleChange}
			formatOptionLabel={formatOptionLabel}
			styles={selectStyles}
			paginationProps={paginationProps}
		/>
	);
};

Filter.propTypes = {
	options: PropTypes.array.isRequired,
	fuzzyOptions: PropTypes.object.isRequired,
	wait: PropTypes.number,
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	dropdownWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	title: PropTypes.string,
	category: PropTypes.string,
	selectedValues: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
	multi: PropTypes.bool,
	selectAll: PropTypes.func,
	deselectAll: PropTypes.func,
	removeFilter: PropTypes.func,
	hideButtons: PropTypes.bool,
	isSearchable: PropTypes.bool,
	textColor: PropTypes.string,
	showCategory: PropTypes.bool,
	handleChange: PropTypes.func,
	onLoadMore: PropTypes.func,
	hasMore: PropTypes.bool,
	isLoadingMore: PropTypes.bool,
};

export default Filter;
